import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/** Admin → System status (backend lib/system-status.ts). Types mirror the backend response. */

export type Tone = "ok" | "warn" | "down" | "unknown"
export type Problem = { tone: "warn" | "down"; area: string; title: string; detail?: string; link?: string }
export type ServiceRow = {
  name: string
  label: string
  running: number
  desired: number
  pending: number
  min: number | null
  max: number | null
  cpu: number | null
  memory: number | null
  cpu_series: (number | null)[]
  deploy: { state: string | null; started_at: string | null; revision: string | null; in_progress: boolean }
  tone: Tone
}
export type JobRow = { name: string; label: string; schedule: string; critical: boolean; last_at: string | null; ms: number | null; ok: boolean | null; error: string | null; state: "ok" | "late" | "failed" | "unknown"; late_minutes: number }
export type SystemStatus = {
  generated_at: string
  env: string | null
  region: string
  aws: { connected: boolean; error: string | null }
  overall: Tone
  problems: Problem[]
  services: ServiceRow[]
  database: null | { id: string; status: string | null; instance_class: string | null; cpu: number | null; cpu_series: (number | null)[]; connections: number | null; free_storage_gb: number | null; allocated_gb: number | null; max_allocated_gb: number | null; free_memory_gb: number | null; read_latency_ms: number | null; write_latency_ms: number | null; tone: Tone }
  redis: null | { id: string; status: string | null; node_type: string | null; cpu: number | null; memory_pct: number | null; connections: number | null; tone: Tone }
  traffic: null | { requests_15m: number | null; errors_15m: number | null; error_rate_15m: number | null; requests_series: (number | null)[]; errors_series: (number | null)[]; payments_failed_1h: number | null; declines_1h: number | null; tone: Tone }
  sites: { id: string; label: string; url: string; status: number | null; ms: number | null; error: string | null; tone: Tone }[]
  alarms: { name: string; state: string; reason: string | null; since: string | null; description: string | null }[]
  jobs: JobRow[]
  store: {
    orders_1h: number | null
    orders_24h: number | null
    last_order_at: string | null
    issues: { open: number; critical: number; high: number; normal: number; latest: { id: string; title: string; severity: string; area: string; last_seen_at: string }[] } | null
    messages_24h: { email_sent: number; email_failed: number; sms_sent: number; sms_failed: number } | null
    shipstation: { failed: number; on_hold: number; pending_over_1h: number } | null
    renewals: { paid_today: number; failed_today: number; overdue: number; processing_stuck: number } | null
    recovery: { id: string; status: string; requested: number; sent: number; failed: number; skipped: number; started_at: string | null; updated_at: string | null } | null
    outboxes: { klaviyo_pending_old: number; conversions_pending_old: number } | null
  }
  errors: Record<string, string>
}

export function useSystemStatus() {
  return useQuery({
    queryKey: ["system-status"],
    queryFn: () => apiFetch<SystemStatus>(`/admin/terify/system-status`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })
}

export const fetchFreshStatus = () => apiFetch<SystemStatus>(`/admin/terify/system-status?fresh=1`)
